import { searchDimensionDictionary } from "../../../../../api/biApi";
import type { DashboardFilter } from "../../dashboard.types";
import { buildFilterValuesRevalidationQuery, getSurvivingSelection, toFilterValue } from "./filterValuesQuery";
import type { FilterWidgetSummary } from "./parentFilterOptions";
import { createParentCondition } from "./parentNarrowing";

const allRowsRange = { skip: 0, take: 0, totals: true };

interface CascadeParams {
  levels: FilterWidgetSummary[][];
  filterWidgets: FilterWidgetSummary[];
  filters: DashboardFilter[];
  selections: Map<string, unknown[]>;
  signal: AbortSignal;
  onWidgetValuesChange: (widgetId: string, values: (string | null)[]) => void;
}

const loadSurvivingValues = async (
  filter: DashboardFilter,
  selectedValues: unknown[],
  parentFilterId: string,
  parentValues: unknown[],
  signal: AbortSignal
): Promise<ReadonlySet<string | null> | undefined> => {
  const parentCondition = createParentCondition(parentFilterId, parentValues);
  if (parentCondition === undefined) {
    return undefined;
  }
  const query = buildFilterValuesRevalidationQuery({ filter, selectedValues, parentCondition });
  if (query === undefined) {
    return undefined;
  }

  const response = await searchDimensionDictionary(filter.dataSetId, { ...query.request, range: allRowsRange }, signal);
  if (!response.success) {
    throw new Error(response.error?.message ?? "Failed to revalidate filter values");
  }

  const keyFieldName = response.data.keyFieldName || filter.valueField || "";
  return new Set(response.data.items.map((item) => toFilterValue(item[keyFieldName])));
};

export const runFilterRevalidationCascade = async ({
  levels,
  filterWidgets,
  filters,
  selections,
  signal,
  onWidgetValuesChange,
}: CascadeParams): Promise<void> => {
  for (const level of levels) {
    if (signal.aborted) {
      return;
    }

    const results = await Promise.allSettled(
      level.map(async (widget) => {
        const filter = filters.find((f) => f.id === widget.dashboardFilterId);
        const parentWidget = filterWidgets.find((w) => w.widgetId === widget.parentWidgetId);
        const parentFilterId = parentWidget?.dashboardFilterId;
        if (filter === undefined || parentWidget === undefined || parentFilterId === undefined) {
          return;
        }

        const selectedValues = selections.get(widget.widgetId) ?? [];
        const survivingValues = await loadSurvivingValues(
          filter,
          selectedValues,
          parentFilterId,
          selections.get(parentWidget.widgetId) ?? [],
          signal
        );
        if (survivingValues === undefined || signal.aborted) {
          return;
        }

        const nextValues = getSurvivingSelection(selectedValues, survivingValues);
        selections.set(widget.widgetId, nextValues);
        if (nextValues.length !== selectedValues.length) {
          onWidgetValuesChange(widget.widgetId, nextValues);
        }
      })
    );

    const failure = results.find((result) => result.status === "rejected");
    if (failure !== undefined && failure.status === "rejected") {
      throw failure.reason instanceof Error ? failure.reason : new Error(String(failure.reason));
    }
  }
};
